import { ActionTree } from 'vuex'

import api from '@/apiClient'

import {
  OptionGroup,
  OptionGroupContext,
  OptionGroupResourcePayload,
  OptionGroupsResourcePayload
} from '@/models'

import { assertToken, mergeById } from '@/utils'

import { RootState } from '../../state'

import {
  ADD_OPTION_GROUP_OPTIONS,
  REMOVE_OPTION_GROUP_OPTIONS
} from '../options/mutations'

import { MenuState } from '../state'

import {
  ADD_OPTION_GROUP,
  INCREMENT_ITEM_GROUP_OPTION_GROUPS_QUERY_INDEX,
  LOADED_ALL_ITEM_GROUP_OPTION_GROUPS,
  REMOVE_ITEM_GROUP_OPTION_GROUP,
  REMOVE_OPTION_GROUP,
  RESET_ITEM_GROUP_OPTION_GROUPS_QUERY_INDEX,
  UPDATE_ITEM_GROUP_OPTION_GROUPS,
  UPDATE_ITEM_GROUP_OPTION_GROUPS_TOTAL,
  UPDATE_OPTION_GROUPS
} from './mutations'

const actions: ActionTree<MenuState, RootState> = {
  /**
   * Adds a new OptionGroup, prepares its relational objects and updates an ItemGroup relation.
   *
   * @param optionGroup OptionGroup to be added.
   * @param itemGroupId ItemGroup id in which the OptionGroup will be added.
   */
  addOptionGroup (
    { commit, state },
    { optionGroup, itemGroupId }: { optionGroup: OptionGroup; itemGroupId: number }
  ) {
    const itemGroupOptionGroups = state.itemGroups.optionGroups[itemGroupId]

    if (itemGroupOptionGroups === undefined) {
      throw new Error(`OptionGroups relation for ItemGroup "${itemGroupId}" was not found`)
    }

    commit(ADD_OPTION_GROUP_OPTIONS, optionGroup.id)

    commit(ADD_OPTION_GROUP, optionGroup)

    commit(UPDATE_ITEM_GROUP_OPTION_GROUPS_TOTAL, {
      itemGroupId,
      total: itemGroupOptionGroups.total + 1
    })

    commit(UPDATE_ITEM_GROUP_OPTION_GROUPS, {
      itemGroupId,
      optionGroups: [...itemGroupOptionGroups.ids, optionGroup.id]
    })
  },

  /**
   * Creates an OptionGroup.
   *
   * It rejects if the user is not logged in.
   *
   * @param payload Payload to create the OptionGroup.
   */
  async createOptionGroup ({ dispatch, rootState, rootGetters }, payload: OptionGroupResourcePayload) {
    const businessUnit = rootState.businessUnit
    const providerId: number | null = rootGetters['auth/currentProvider']

    if (providerId === null) {
      throw new Error('User is not authenticated')
    }

    if (businessUnit === null) {
      throw new Error('BusinessUnit is not loaded')
    }

    const tokenV2 = assertToken(rootState.auth.tokenV2)

    const optionGroup = await api.menu.optionGroups.createOptionGroup(tokenV2, providerId, businessUnit.id, payload)

    await dispatch('addOptionGroup', { optionGroup, itemGroupId: payload.groupId })

    return optionGroup
  },

  /**
   * Loads one OptionGroup.
   *
   * It rejects if the user is not logged in.
   *
   * It loads the ItemGroup if it is not loaded.
   *
   * @param context OptionGroupContext to be loaded.
   */
  async loadOptionGroup (
    { commit, dispatch, rootState, rootGetters, state },
    { id, itemGroupId }: OptionGroupContext
  ) {
    const businessUnit = rootState.businessUnit
    const providerId: number | null = rootGetters['auth/currentProvider']

    if (providerId === null) {
      throw new Error('User is not authenticated')
    }

    if (businessUnit === null) {
      throw new Error('BusinessUnit is not loaded')
    }

    const tokenV2 = assertToken(rootState.auth.tokenV2)

    let itemGroupOptionGroups = state.itemGroups.optionGroups[itemGroupId]

    if (itemGroupOptionGroups === undefined) {
      await dispatch('loadItemGroup', itemGroupId)
      itemGroupOptionGroups = state.itemGroups.optionGroups[itemGroupId]
    }

    const optionGroup = await api.menu.optionGroups.fetchOptionGroup(tokenV2, providerId, businessUnit.id, id)

    if (!itemGroupOptionGroups.ids.has(optionGroup.id)) {
      await dispatch('addOptionGroup', { optionGroup, itemGroupId })
    } else {
      const optionGroups = mergeById(state.optionGroups.elements, [optionGroup])
      commit(UPDATE_OPTION_GROUPS, optionGroups)
    }
  },

  /**
   * Loads a page of OptionGroups.
   *
   * It rejects if the user is not logged in.
   *
   * It loads the ItemGroup if it is not loaded.
   *
   * It prepares the relational objects for each loaded OptionGroup.
   *
   * When a previously loaded page changes, it desynchronizes and will load
   * an empty page after the last page, which will reset the query index.
   *
   * @param itemGroupId ItemGroup id whose OptionGroups will be loaded.
   */
  async loadOptionGroupPage (
    { commit, dispatch, rootState, rootGetters, state },
    itemGroupId: number
  ) {
    const businessUnit = rootState.businessUnit
    const providerId: number | null = rootGetters['auth/currentProvider']

    if (providerId === null) {
      throw new Error('User is not authenticated')
    }

    if (businessUnit === null) {
      throw new Error('BusinessUnit is not loaded')
    }

    let itemGroupOptionGroups = state.itemGroups.optionGroups[itemGroupId]

    if (itemGroupOptionGroups === undefined) {
      await dispatch('loadItemGroup', itemGroupId)
      itemGroupOptionGroups = state.itemGroups.optionGroups[itemGroupId]
    }

    const tokenV2 = assertToken(rootState.auth.tokenV2)

    const page = await api.menu.optionGroups.fetchOptionGroupPage(
      tokenV2,
      providerId,
      businessUnit.id,
      {
        ...itemGroupOptionGroups.query,
        groupId: itemGroupId
      }
    )

    if (itemGroupOptionGroups.total !== page.total) {
      commit(UPDATE_ITEM_GROUP_OPTION_GROUPS_TOTAL, {
        itemGroupId,
        total: page.total
      })
    }

    if (page.elements.length > 0) {
      for (const optionGroup of page.elements) {
        if (state.optionGroups.options[optionGroup.id] === undefined) {
          commit(ADD_OPTION_GROUP_OPTIONS, optionGroup.id)
        }
      }

      const optionGroups = mergeById(state.optionGroups.elements, page.elements)

      commit(UPDATE_OPTION_GROUPS, optionGroups)

      const ids = [
        ...itemGroupOptionGroups.ids,
        ...page.elements.map((i) => i.id)
      ]

      commit(UPDATE_ITEM_GROUP_OPTION_GROUPS, {
        itemGroupId,
        optionGroups: ids
      })

      if ([...new Set(ids)].length === page.total) {
        commit(LOADED_ALL_ITEM_GROUP_OPTION_GROUPS, itemGroupId)
      }

      commit(INCREMENT_ITEM_GROUP_OPTION_GROUPS_QUERY_INDEX, {
        itemGroupId,
        offset: page.elements.length
      })
    } else {
      commit(RESET_ITEM_GROUP_OPTION_GROUPS_QUERY_INDEX, itemGroupId)

      if (page.total === 0 && itemGroupOptionGroups.ids.size > 0) {
        commit(UPDATE_ITEM_GROUP_OPTION_GROUPS, {
          itemGroupId,
          optionGroups: []
        })
      }
    }
  },

  /**
   * Updates an OptionGroup.
   *
   * It rejects if the user is not logged in.
   *
   * @param id OptionGroup id to be updated.
   * @param payload Payload to update it.
   */
  async updateOptionGroup (
    { commit, rootState, rootGetters, state },
    { id, payload }: { id: number; payload: OptionGroupResourcePayload }
  ) {
    const businessUnit = rootState.businessUnit
    const providerId: number | null = rootGetters['auth/currentProvider']

    if (providerId === null) {
      throw new Error('User is not authenticated')
    }

    if (businessUnit === null) {
      throw new Error('BusinessUnit is not loaded')
    }

    const tokenV2 = assertToken(rootState.auth.tokenV2)

    const optionGroup = await api.menu.optionGroups.updateOptionGroup(tokenV2, providerId, businessUnit.id, id, payload)

    const optionGroups = mergeById(state.optionGroups.elements, [optionGroup])

    commit(UPDATE_OPTION_GROUPS, optionGroups)
  },

  /**
   * Updates sequences of OptionGroups.
   *
   * It rejects if the user is not logged in.
   *
   * @param itemGroupId ItemGroup id to be updates OptionGroups sequences.
   * @param payload Payload to update it.
   */
  async updateSequences (
    { commit, rootState, rootGetters, state },
    { itemGroupId, payload }: { itemGroupId: number; payload: OptionGroupsResourcePayload[] }
  ) {
    const businessUnit = rootState.businessUnit
    const providerId: number | null = rootGetters['auth/currentProvider']

    if (providerId === null) {
      throw new Error('User is not authenticated')
    }

    if (businessUnit === null) {
      throw new Error('BusinessUnit is not loaded')
    }

    const tokenV2 = assertToken(rootState.auth.tokenV2)

    const page = await api.menu.optionGroups.updateSequences(tokenV2, providerId, businessUnit.id, itemGroupId, payload)

    const optionGroups = mergeById(state.optionGroups.elements, page.elements)

    commit(UPDATE_OPTION_GROUPS, optionGroups)
  },

  /**
   * Removes an OptionGroup and its relational objects.
   *
   * It rejects if the user is not logged in.
   *
   * @param context OptionGroupContext to be removed.
   */
  async removeOptionGroup (
    { commit, rootState, rootGetters, state },
    { id, itemGroupId }: OptionGroupContext
  ) {
    const businessUnit = rootState.businessUnit
    const providerId: number | null = rootGetters['auth/currentProvider']

    if (providerId === null) {
      throw new Error('User is not authenticated')
    }

    if (businessUnit === null) {
      throw new Error('BusinessUnit is not loaded')
    }

    const tokenV2 = assertToken(rootState.auth.tokenV2)

    const itemGroupOptionGroups = state.itemGroups.optionGroups[itemGroupId]

    if (itemGroupOptionGroups === undefined) {
      throw new Error(`OptionGroups relation for ItemGroup "${itemGroupId}" was not found`)
    }

    await api.menu.optionGroups.deleteOptionGroup(tokenV2, providerId, businessUnit.id, id)

    commit(UPDATE_ITEM_GROUP_OPTION_GROUPS_TOTAL, {
      itemGroupId,
      total: itemGroupOptionGroups.total - 1
    })

    commit(REMOVE_ITEM_GROUP_OPTION_GROUP, { id, itemGroupId })

    commit(REMOVE_OPTION_GROUP, id)

    commit(REMOVE_OPTION_GROUP_OPTIONS, id)
  }
}

export default actions
